import { useAuthStore } from "@features/auth/store/authStore";
import QuestionBankPassageAdapter from "@features/question-bank/components/QuestionBankPassageAdapter";
import TutorialVideoUploader from "@features/tutorial-bank/components/TutorialVideoUploader";
import {
  EditorInputField,
  EditorSection,
  EditorTextField,
} from "@shared/components/editors/EditorFields";
import { ImageUploadButtons } from "@shared/components/ImageUploadButtons";
import theme from "@shared/utils/themeColors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useReducer, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "../../../../components/shared/ConfirmationModal";
import AuthenticatedLayout from "../../../../shared/layouts/AuthenticatedLayout";
import QuestionBankItemHeader from "../../../question-bank/components/QuestionBankItemHeader";
import QuestionBankQuestionAdapter from "../../../question-bank/components/QuestionBankQuestionAdapter";
import { CategoryService } from "../../../question-bank/services/category.service";
import {
  QuestionBankItem,
  QuestionBankPassage,
  QuestionBankQuestion,
} from "../../../question-bank/types/question-bank.types";
import { tutorialBankKeys } from "../../hooks/useTutorialBank";
import { TutorialBankEditorService } from "../../services/tutorial-bank-editor.service";
import {
  TutorialBankItem,
  TutorialBankItemStatus,
  TutorialVideo,
  VideoSourceType,
} from "../../types/tutorial-bank.types";

// Define action types for the reducer
type EditorAction =
  | { type: "SET_ITEM"; payload: TutorialBankItem }
  | { type: "UPDATE_TITLE"; payload: string }
  | { type: "UPDATE_SUBTITLE"; payload: string }
  | {
      type: "UPDATE_INTRODUCTION";
      payload: { title?: string; description?: string };
    }
  | { type: "UPDATE_DIRECTIONS"; payload: string }
  | { type: "UPDATE_DIFFICULTY"; payload: "easy" | "medium" | "hard" }
  | { type: "UPDATE_TAGS"; payload: string[] }
  | { type: "UPDATE_CATEGORY"; payload: string }
  | {
      type: "UPDATE_VIDEO";
      payload: {
        url?: string;
        title?: string;
        description?: string;
      };
    }
  | {
      type: "UPDATE_PASSAGE";
      payload: { passageIndex: number; passage: QuestionBankPassage };
    }
  | { type: "ADD_PASSAGE"; payload: QuestionBankPassage }
  | { type: "REMOVE_PASSAGE"; payload: number }
  | {
      type: "UPDATE_QUESTION";
      payload: QuestionBankQuestion;
    }
  | { type: "REMOVE_QUESTION" }
  | { type: "UPDATE_STATUS"; payload: TutorialBankItemStatus }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "SAVE_HISTORY" }
  | { type: "SERVER_SAVE"; payload: TutorialBankItem }
  | { type: "REMOVE_VIDEO" };

// Define the state type
interface EditorState {
  item: TutorialBankItem | null;
  history: TutorialBankItem[];
  currentHistoryIndex: number;
  isDirty: boolean;
  lastSavedIndex: number; // Track the last index that was saved to the server
}

// Initial state
const initialState: EditorState = {
  item: null,
  history: [],
  currentHistoryIndex: -1,
  isDirty: false,
  lastSavedIndex: -1,
};

// Reducer function
function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "SET_ITEM":
      return {
        ...state,
        item: action.payload,
        history: [action.payload],
        currentHistoryIndex: 0,
        isDirty: false,
        lastSavedIndex: 0, // Initial load is considered saved
      };
    case "UPDATE_TITLE":
      if (!state.item) return state;
      const updatedItem = {
        ...state.item,
        header: {
          ...state.item.header,
          title: action.payload,
        },
      };
      return {
        ...state,
        item: updatedItem,
        isDirty: true,
      };
    case "UPDATE_SUBTITLE":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          header: {
            ...state.item.header,
            subtitle: action.payload,
          },
        },
        isDirty: true,
      };
    case "UPDATE_INTRODUCTION":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          introduction: {
            ...state.item.introduction,
            ...action.payload,
          },
        },
        isDirty: true,
      };
    case "UPDATE_DIRECTIONS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          directions: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_DIFFICULTY":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          difficulty: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_TAGS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          tags: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_CATEGORY":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          categoryId: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_STATUS":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          status: action.payload,
        },
        isDirty: true,
      };
    case "UPDATE_VIDEO":
      if (!state.item) return state;

      // Create default video object if it doesn't exist yet
      const videoBase: TutorialVideo = state.item.video || {
        id: uuidv4(),
        tutorialBankItemId: state.item.id || "",
        url: "",
        title: "",
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      return {
        ...state,
        item: {
          ...state.item,
          video: {
            ...videoBase,
            ...action.payload,
            updatedAt: Date.now(),
          },
        },
        isDirty: true,
      };
    case "REMOVE_VIDEO":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          video: null,
        },
        isDirty: true,
      };
    case "UPDATE_PASSAGE":
      if (!state.item || !state.item.passages) return state;
      const updatedPassages = [...state.item.passages];
      updatedPassages[action.payload.passageIndex] = action.payload.passage;
      return {
        ...state,
        item: {
          ...state.item,
          passages: updatedPassages,
        },
        isDirty: true,
      };
    case "ADD_PASSAGE":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          passages: [...(state.item.passages || []), action.payload],
        },
        isDirty: true,
      };
    case "REMOVE_PASSAGE":
      if (!state.item || !state.item.passages) return state;
      const passagesAfterRemoval = [...state.item.passages];
      passagesAfterRemoval.splice(action.payload, 1);
      return {
        ...state,
        item: {
          ...state.item,
          passages: passagesAfterRemoval,
        },
        isDirty: true,
      };
    case "UPDATE_QUESTION":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          question: action.payload,
        },
        isDirty: true,
      };
    case "REMOVE_QUESTION":
      if (!state.item) return state;
      return {
        ...state,
        item: {
          ...state.item,
          question: undefined,
        },
        isDirty: true,
      };
    case "SAVE_HISTORY":
      if (!state.item || !state.isDirty) return state;

      // Save current item to history
      const newHistory = [
        ...state.history.slice(0, state.currentHistoryIndex + 1),
        state.item,
      ];

      return {
        ...state,
        history: newHistory,
        currentHistoryIndex: newHistory.length - 1,
        isDirty: true, // Still dirty, since we're only saving to history, not server
      };
    case "UNDO":
      if (state.currentHistoryIndex <= 0) return state;
      return {
        ...state,
        item: state.history[state.currentHistoryIndex - 1],
        currentHistoryIndex: state.currentHistoryIndex - 1,
        isDirty: state.currentHistoryIndex - 1 !== state.lastSavedIndex,
      };
    case "REDO":
      if (state.currentHistoryIndex >= state.history.length - 1) return state;
      return {
        ...state,
        item: state.history[state.currentHistoryIndex + 1],
        currentHistoryIndex: state.currentHistoryIndex + 1,
        isDirty: state.currentHistoryIndex + 1 !== state.lastSavedIndex,
      };
    case "SERVER_SAVE":
      if (!state.item) return state;

      console.log("state", state, action.payload);

      return {
        ...state,
        item: action.payload,
        history: [...state.history, action.payload],
        currentHistoryIndex: state.history.length,
        lastSavedIndex: state.history.length,
        isDirty: false,
      };
    default:
      return state;
  }
}

// Utility function to preserve scroll position during updates
function preserveScrollPosition(callback: () => void) {
  // Find the editor container
  const editorContainer = document.querySelector(".editor-container");
  const scrollContainer = editorContainer || window;

  // Get the current scroll position
  const scrollTop = scrollContainer ? (scrollContainer as any).scrollTop : 0;

  // Execute the callback that will update state
  callback();

  // Use requestAnimationFrame to restore scroll position after DOM updates
  requestAnimationFrame(() => {
    // Double requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      // Restore scroll position on the specific container
      if (scrollContainer) {
        (scrollContainer as any).scrollTop = scrollTop;
      }
    });
  });
}

export default function TutorialBankItemEditor() {
  const { itemId } = useParams<{ itemId: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<{
    show: boolean;
    passageIndex: number;
  }>({ show: false, passageIndex: -1 });
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  // Check if we're in create mode
  const isCreateMode = !itemId;

  // Initialize category service
  const categoryService = new CategoryService();

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ["question-bank-categories"],
    queryFn: () => categoryService.getCategoryHierarchy(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create a new item if in create mode
  useEffect(() => {
    if (isCreateMode && user) {
      const now = Date.now();
      const newItem: TutorialBankItem = {
        categoryId: "",
        header: {
          title: "",
          subtitle: "",
        },
        introduction: {
          title: "",
          description: "",
        },
        directions: "",
        passages: [],
        question: {
          questionBankItemId: uuidv4(),
          text: "",
          options: {},
          correctAnswer: "",
          createdAt: now,
          updatedAt: now,
        },
        video: undefined,
        difficulty: "medium",
        tags: [],
        createdAt: now,
        updatedAt: now,
        createdBy: user.uid,
        isActive: true,
        status: TutorialBankItemStatus.DRAFT,
        isDeleted: false,
      };

      dispatch({ type: "SET_ITEM", payload: newItem });
    }
  }, [isCreateMode, user]);

  // Fetch item data only if we're in edit mode
  const {
    data: item,
    isLoading,
    error,
  } = useQuery({
    queryKey: tutorialBankKeys.detail(itemId!),
    queryFn: () =>
      TutorialBankEditorService.getTutorialBankItemForEditing(itemId!),
    enabled: !isCreateMode,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Initialize state with fetched item
  useEffect(() => {
    if (item) {
      if (!item.question) {
        const now = Date.now();
        const itemWithQuestion = {
          ...item,
          question: {
            id: uuidv4(),
            questionBankItemId: item.id,
            text: "New Question",
            options: {},
            correctAnswer: "",
            createdAt: now,
            updatedAt: now,
          },
        };
        dispatch({ type: "SET_ITEM", payload: itemWithQuestion });
      } else {
        dispatch({ type: "SET_ITEM", payload: item });
      }
    }
  }, [item]);

  // Save history when changes are made
  useEffect(() => {
    if (state.isDirty) {
      const timer = setTimeout(() => {
        // We're only saving to history, not to the server
        // so we use a new action type that doesn't affect isDirty
        dispatch({ type: "SAVE_HISTORY" });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [state.isDirty, state.item]);

  // Effect to handle beforeunload event to prevent accidental window closing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isDirty) {
        // Standard way to show a confirmation dialog before closing the window
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state.isDirty]);

  // Handle changing the status (draft/published)
  const handleStatusChange = (isPublished: boolean) => {
    dispatch({
      type: "UPDATE_STATUS",
      payload: isPublished
        ? TutorialBankItemStatus.PUBLISHED
        : TutorialBankItemStatus.DRAFT,
    });
  };

  // Handle save
  const handleSave = async () => {
    // Validate that a category is selected
    if (!state.item?.categoryId) {
      toast.error("Please select a category before saving");
      return;
    }

    console.log("Item before save:", state);

    // Validate that a correct answer is selected
    if (state.item?.question) {
      // Check if the question has options
      const hasOptions = Array.isArray(state.item.question.options)
        ? state.item.question.options.length > 0
        : Object.keys(state.item.question.options).length > 0;

      // Check if a correct answer is selected
      if (hasOptions && !state.item.question.correctAnswer) {
        toast.error(
          "Please select a correct answer for the question before saving"
        );
        return;
      }
    }

    try {
      setIsSaving(true);
      let savedItem;
      if (isCreateMode) {
        // Create new item
        savedItem = await TutorialBankEditorService.createTutorialBankItem(
          state.item
        );

        // Navigate to edit page for the newly created item
        navigate(`/tutorial-bank/admin/edit/${savedItem.id}`);
      } else {
        // Update existing item
        savedItem = await TutorialBankEditorService.saveTutorialBankItem(
          itemId!,
          state.item
        );
      }

      // Update state with saved item
      dispatch({ type: "SERVER_SAVE", payload: savedItem });
      toast.success("Tutorial saved successfully");
    } catch (error: any) {
      console.error("Error saving tutorial:", error);
      toast.error(
        `Failed to ${isCreateMode ? "create" : "save"} tutorial: ${
          error.message
        }`
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation prompts for unsaved changes
  const handleBack = () => {
    if (state.isDirty) {
      setShowUnsavedChanges(true);
    } else {
      navigate("/tutorial-bank/admin/list");
    }
  };

  // Handle confirmation for unsaved changes
  const handleConfirmDiscardChanges = () => {
    setShowUnsavedChanges(false);
    navigate("/tutorial-bank/admin/list");
  };

  // Handle close for unsaved changes modal
  const handleCloseUnsavedChanges = () => {
    setShowUnsavedChanges(false);
  };

  // Handle passage delete confirmation
  const handleConfirmPassageDelete = () => {
    if (showDeleteConfirmation.passageIndex >= 0) {
      dispatch({
        type: "REMOVE_PASSAGE",
        payload: showDeleteConfirmation.passageIndex,
      });
      setShowDeleteConfirmation({ show: false, passageIndex: -1 });
    }
  };

  // Handle video remove
  const handleRemoveVideo = () => {
    dispatch({ type: "REMOVE_VIDEO" });
  };

  // Handle video upload
  const handleVideoUpload = async (file: File) => {
    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    try {
      setIsVideoUploading(true);

      // Upload the video file and get both video URL and thumbnail URL
      const { videoUrl, thumbnailUrl } =
        await TutorialBankEditorService.uploadTutorialVideo(
          user.uid,
          file,
          (progress) => {
            console.log(`Upload progress: ${progress}%`);
          }
        );

      // Create a new video object with thumbnail
      const videoData: TutorialVideo = {
        id: uuidv4(),
        tutorialBankItemId: state.item?.id || "",
        url: videoUrl,
        thumbnailUrl: thumbnailUrl, // Add the thumbnail URL
        title: file.name.split(".")[0] || "Tutorial Video",
        description: "",
        sourceType: VideoSourceType.UPLOADED,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      // Update state with the new video
      dispatch({ type: "UPDATE_VIDEO", payload: videoData });

      toast.success(
        "Video uploaded successfully" + (thumbnailUrl ? " with thumbnail" : "")
      );
    } catch (error: any) {
      console.error("Error uploading video:", error);
      toast.error(
        `Failed to upload video: ${error.message || "Unknown error"}`
      );
    } finally {
      setIsVideoUploading(false);
    }
  };

  // Handle external video addition
  const handleExternalVideoAdd = (videoData: Partial<TutorialVideo>) => {
    if (!state.item) return;

    // Create a complete video object from the partial data
    const completeVideoData: TutorialVideo = {
      id: videoData.id || uuidv4(),
      tutorialBankItemId: state.item.id || "",
      url: videoData.url || "",
      thumbnailUrl: videoData.thumbnailUrl,
      title: videoData.title || "External Video",
      description: videoData.description || "",
      sourceType:
        videoData.sourceType ||
        (videoData.url?.includes("youtube.com")
          ? VideoSourceType.YOUTUBE
          : videoData.url?.includes("vimeo.com")
          ? VideoSourceType.VIMEO
          : VideoSourceType.EXTERNAL),
      createdAt: videoData.createdAt || Date.now(),
      updatedAt: videoData.updatedAt || Date.now(),
    };

    // Update state with the new video
    dispatch({ type: "UPDATE_VIDEO", payload: completeVideoData });
    toast.success("External video added successfully");
  };

  // Handle video details update
  const handleVideoDetailsUpdate = (title: string, description?: string) => {
    preserveScrollPosition(() => {
      dispatch({
        type: "UPDATE_VIDEO",
        payload: {
          title,
          description,
        },
      });
    });
  };

  // Handle question update
  const handleQuestionUpdate = (question: QuestionBankQuestion) => {
    preserveScrollPosition(() => {
      dispatch({ type: "UPDATE_QUESTION", payload: question });
    });
  };

  const handleRemoveQuestion = () => {
    dispatch({ type: "REMOVE_QUESTION" });
  };

  const handleAddPassage = () => {
    if (!state.item) return;

    const now = Date.now();
    const newPassage: QuestionBankPassage = {
      id: uuidv4(),
      questionBankItemId: state.item.id,
      title: "New Passage",
      lines: [],
      orderIndex: state.item.passages ? state.item.passages.length : 0,
      createdAt: now,
      updatedAt: now,
    };

    dispatch({ type: "ADD_PASSAGE", payload: newPassage });
  };

  return (
    <AuthenticatedLayout isLoading={isLoading} error={error}>
      {state.item && (
        <>
          <div className="flex justify-between mb-4">
            <QuestionBankItemHeader
              item={state.item as unknown as QuestionBankItem}
              onChangeStatus={handleStatusChange}
              onSave={handleSave}
              onUndo={() => dispatch({ type: "UNDO" })}
              onRedo={() => dispatch({ type: "REDO" })}
              onBack={() => handleBack()}
              canUndo={state.currentHistoryIndex > 0}
              canRedo={state.currentHistoryIndex < state.history.length - 1}
              isSaving={isSaving}
              isDirty={state.isDirty}
              categories={categories}
              onCategoryChange={(categoryId) =>
                dispatch({ type: "UPDATE_CATEGORY", payload: categoryId })
              }
              onDifficultyChange={(difficulty) =>
                dispatch({ type: "UPDATE_DIFFICULTY", payload: difficulty })
              }
            />
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col space-y-4 p-4`}
            >
              {/* Header Fields */}
              <EditorSection title="Header">
                <EditorInputField
                  label="Title"
                  value={state.item.header.title}
                  onChange={(value) =>
                    dispatch({ type: "UPDATE_TITLE", payload: value })
                  }
                  placeholder="Item title"
                />
                <EditorTextField
                  label="Subtitle"
                  value={state.item.header.subtitle || ""}
                  onChange={(value) =>
                    dispatch({ type: "UPDATE_SUBTITLE", payload: value })
                  }
                  placeholder="Item subtitle"
                  rows={2}
                />
              </EditorSection>

              {/* Video section */}
              <EditorSection title="Tutorial Video">
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Upload an MP4 video or add a YouTube, Vimeo, or direct MP4
                  link for this tutorial. The video will be displayed at the top
                  of the tutorial page.
                </p>
                <TutorialVideoUploader
                  video={state.item.video}
                  onRemoveVideo={handleRemoveVideo}
                  onUpload={handleVideoUpload}
                  onTitleChange={(title) =>
                    handleVideoDetailsUpdate(
                      title,
                      state.item.video?.description
                    )
                  }
                  onDescriptionChange={(description) =>
                    handleVideoDetailsUpdate(
                      state.item.video?.title || "",
                      description
                    )
                  }
                  isUploading={isVideoUploading}
                  onExternalVideoAdd={handleExternalVideoAdd}
                />
              </EditorSection>

              {/* Directions Fields */}
              <EditorSection title="Directions">
                <EditorTextField
                  value={state.item.directions || ""}
                  onChange={(value) =>
                    dispatch({ type: "UPDATE_DIRECTIONS", payload: value })
                  }
                  placeholder="Directions text"
                  rows={3}
                />
                <ImageUploadButtons
                  onImageUploaded={(formattedTag) => {
                    const updatedText = state.item.directions
                      ? `${state.item.directions}\n${formattedTag}`
                      : formattedTag;

                    dispatch({
                      type: "UPDATE_DIRECTIONS",
                      payload: updatedText,
                    });
                  }}
                />
              </EditorSection>

              {/* Introduction Fields */}
              <EditorSection title="Introduction">
                <EditorInputField
                  label="Title"
                  value={state.item.introduction?.title || ""}
                  onChange={(value) =>
                    dispatch({
                      type: "UPDATE_INTRODUCTION",
                      payload: { title: value },
                    })
                  }
                  placeholder="Introduction title"
                />
                <EditorTextField
                  label="Description"
                  value={state.item.introduction?.description || ""}
                  onChange={(value) =>
                    dispatch({
                      type: "UPDATE_INTRODUCTION",
                      payload: { description: value },
                    })
                  }
                  placeholder="Introduction text"
                />
                <ImageUploadButtons
                  onImageUploaded={(formattedTag) => {
                    const updatedText = state.item.introduction?.description
                      ? `${state.item.introduction?.description}\n${formattedTag}`
                      : formattedTag;

                    dispatch({
                      type: "UPDATE_INTRODUCTION",
                      payload: { description: updatedText },
                    });
                  }}
                />
              </EditorSection>

              <div className="flex flex-col space-y-4 items-end">
                {state.item.passages &&
                  state.item.passages.map((passage, index) => (
                    <QuestionBankPassageAdapter
                      key={passage.id}
                      passage={passage}
                      passageIndex={index}
                      onUpdate={(updatedPassage) =>
                        dispatch({
                          type: "UPDATE_PASSAGE",
                          payload: {
                            passageIndex: index,
                            passage: updatedPassage,
                          },
                        })
                      }
                      onRemove={() =>
                        dispatch({ type: "REMOVE_PASSAGE", payload: index })
                      }
                      isExpanded={true}
                      itemId={state.item.id}
                    />
                  ))}
                <button
                  onClick={handleAddPassage}
                  className={`${theme.button.primary} w-[140px]`}
                >
                  Add Passage
                </button>
              </div>

              <div className="flex flex-col space-y-4 items-end">
                {state.item.question && (
                  <>
                    <QuestionBankQuestionAdapter
                      question={state.item.question}
                      questionIndex={0}
                      onUpdate={handleQuestionUpdate}
                      onRemove={handleRemoveQuestion}
                      userId={user.uid}
                      isExpanded={true}
                    />
                    <div className="flex flex-col space-y-4 items-end w-full">
                      <EditorTextField
                        label="Explanation"
                        value={state.item.question.explanation || ""}
                        onChange={(value) => {
                          if (state.item.question) {
                            const updatedQuestion = {
                              ...state.item.question,
                              explanation: value,
                            };
                            dispatch({
                              type: "UPDATE_QUESTION",
                              payload: updatedQuestion,
                            });
                          }
                        }}
                        placeholder="Explanation for the correct answer"
                      />
                      <ImageUploadButtons
                        className="flex items-center w-full"
                        onImageUploaded={(formattedTag) => {
                          const updatedText = state.item.question?.explanation
                            ? `${state.item.question?.explanation}\n${formattedTag}`
                            : formattedTag;

                          const updatedQuestion = {
                            ...state.item.question,
                            explanation: updatedText,
                          };

                          dispatch({
                            type: "UPDATE_QUESTION",
                            payload: updatedQuestion,
                          });
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Unsaved changes confirmation modal */}
      <ConfirmationModal
        isOpen={showUnsavedChanges}
        onClose={handleCloseUnsavedChanges}
        onConfirm={async () => {
          handleConfirmDiscardChanges();
        }}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        isLoading={false}
      />

      {/* Delete passage confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation.show}
        onClose={() =>
          setShowDeleteConfirmation({ show: false, passageIndex: -1 })
        }
        onConfirm={async () => {
          handleConfirmPassageDelete();
        }}
        title="Delete Passage"
        message="Are you sure you want to delete this passage? This action cannot be undone."
        isLoading={false}
      />
    </AuthenticatedLayout>
  );
}
